import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, formatHora, hoyCaracas } from '../utils/format';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import BotonAyuda from '../components/BotonAyuda';
import { filaClickeable, FOCO_FILA } from '../utils/filaClickeable';

// Caja · Resumen del día (pendiente #91): una pestaña por turno del día y
// una de Total. Solo lectura; el cierre/arqueo sigue en la pantalla de venta.

const TURNO_LABEL = { unico: 'Único', diurno: 'Diurno', nocturno: 'Nocturno' };
const CANAL_LABEL = { mostrador: 'Mostrador', delivery: 'Delivery' };

function hora(valor) {
  return formatHora(valor) || '—';
}

function Tarjeta({ etiqueta, usd, bs, valor }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      <p className="text-xs text-slate-500">{etiqueta}</p>
      {valor != null
        ? <p className="text-xl font-semibold text-slate-800 mt-1">{valor}</p>
        : (
          <>
            <p className="text-xl font-semibold text-slate-800 mt-1">${formatMonto(usd)}</p>
            <p className="text-xs text-slate-500">Bs {formatMonto(bs)}</p>
          </>
        )}
    </div>
  );
}

// Fiscal = base + IVA declarado; Paralelo = el total completo es venta (IVA no se declara).
function DivisionFiscal({ fiscal, paralelo }) {
  if (!fiscal || !paralelo) return null;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="bg-white rounded-xl shadow-sm p-4">
        <p className="text-sm font-semibold text-slate-700">Fiscal <span className="font-normal text-slate-400">· {fiscal.tickets} {fiscal.tickets === 1 ? 'ticket' : 'tickets'}</span></p>
        <div className="mt-2 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Base</span><span className="tabular-nums">${formatMonto(fiscal.base_usd)} · Bs {formatMonto(fiscal.base_bs)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">IVA declarado</span><span className="tabular-nums">${formatMonto(fiscal.iva_usd)} · Bs {formatMonto(fiscal.iva_bs)}</span></div>
          <div className="flex justify-between font-semibold border-t pt-1"><span>Total</span><span className="tabular-nums">${formatMonto(fiscal.total_usd)} · Bs {formatMonto(fiscal.total_bs)}</span></div>
        </div>
      </div>
      <div className="bg-slate-800 text-white rounded-xl shadow-sm p-4">
        <p className="text-sm font-semibold">Paralelo <span className="font-normal text-slate-300">· {paralelo.tickets} {paralelo.tickets === 1 ? 'ticket' : 'tickets'}</span></p>
        <div className="mt-2 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-slate-300">IVA</span><span className="tabular-nums">no se declara</span></div>
          <div className="flex justify-between font-semibold border-t border-slate-600 pt-1 mt-6"><span>Venta</span><span className="tabular-nums">${formatMonto(paralelo.venta_usd)} · Bs {formatMonto(paralelo.venta_bs)}</span></div>
        </div>
      </div>
    </div>
  );
}

function TablaMontos({ titulo, filas, vacio }) {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <h2 className="font-semibold text-slate-700 p-4 border-b">{titulo}</h2>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="p-3">Concepto</th>
            <th className="p-3 text-right">Tickets</th>
            <th className="p-3 text-right">$</th>
            <th className="p-3 text-right">Bs</th>
          </tr>
        </thead>
        <tbody>
          {filas.map((f) => (
            <tr key={f.clave} className="border-t">
              <td className="p-3">{f.nombre}</td>
              <td className="p-3 text-right tabular-nums">{f.tickets ?? '—'}</td>
              <td className="p-3 text-right tabular-nums">{formatMonto(f.total_usd)}</td>
              <td className="p-3 text-right tabular-nums">{formatMonto(f.total_bs)}</td>
            </tr>
          ))}
          {filas.length === 0 && (
            <tr><td colSpan={4} className="p-6 text-center text-slate-400">{vacio}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function DetalleTicket({ id }) {
  const [detalle, setDetalle] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    api.get(`/caja/venta/${id}`)
      .then(({ data }) => setDetalle(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el ticket.'));
  }, [id]);

  if (error) return <p className="text-red-600 text-sm">{error}</p>;
  if (!detalle) return <p className="text-slate-400 text-sm">Cargando…</p>;
  return (
    <div className="grid md:grid-cols-2 gap-4 text-sm">
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">Productos</p>
        {detalle.lineas.map((l) => (
          <div key={l.id} className={`flex justify-between ${l.linea_padre_id ? 'pl-4 text-slate-500 text-xs' : ''}`}>
            <span>{l.linea_padre_id ? '+ ' : ''}{Number.isInteger(Number(l.cantidad)) ? Number(l.cantidad) : formatMonto(l.cantidad)} × {l.nombre}</span>
            <span className="tabular-nums">${formatMonto(l.subtotal_usd)}</span>
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs font-medium text-slate-500 mb-1">Pagos</p>
        {detalle.pagos.map((p, i) => (
          <div key={i} className="flex justify-between">
            <span>{p.es_vuelto ? 'Vuelto · ' : ''}{p.nombre}</span>
            <span className="tabular-nums">{p.moneda === 'USD' ? '$' : 'Bs'} {formatMonto(p.monto)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CajaResumen() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [fecha, setFecha] = useState(hoyCaracas());
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [pestana, setPestana] = useState('total');
  const [abierto, setAbierto] = useState(null); // id de ticket con el detalle desplegado

  useEffect(() => {
    if (!empresaActiva) return;
    setError('');
    setAbierto(null);
    api.get('/caja/resumen-dia', { params: { fecha } })
      .then(({ data }) => {
        setDatos(data);
        // Si la pestaña elegida no existe ese día, volver a Total.
        setPestana((p) => (p === 'total' || data.turnos.some((t) => String(t.id) === p) ? p : 'total'));
      })
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el resumen.'));
  }, [empresaActiva, fecha]);

  const pestanas = useMemo(() => [
    ...(datos?.turnos || []).map((t) => ({ key: String(t.id), label: `${TURNO_LABEL[t.turno] || t.turno}${t.estado === 'abierto' ? ' · abierto' : ''}` })),
    { key: 'total', label: 'Total del día' }
  ], [datos]);

  const turnoSel = datos?.turnos.find((t) => String(t.id) === pestana) || null;
  const vista = turnoSel || datos?.total;
  const tickets = (datos?.tickets || []).filter((t) => !turnoSel || t.cierre_id === turnoSel.id);
  const nombrePestana = pestanas.find((p) => p.key === pestana)?.label || 'Total del día';

  // La pestaña que se ve (turno o total del día), en $ y Bs como en pantalla.
  function contenidoExcel() {
    const cols = [{ titulo: 'Concepto' }, { titulo: 'Tickets', tipo: 'entero' }, { titulo: '$', tipo: 'monto' }, { titulo: 'Bs', tipo: 'monto' }];
    const resumen = vista ? [
      ['Tickets cobrados', vista.ventas.tickets, null, null],
      ...(vista.ventas.anuladas ? [['Tickets anulados', vista.ventas.anuladas, null, null]] : []),
      ['Total vendido', null, Number(vista.ventas.total_usd), Number(vista.ventas.total_bs)],
      ['IVA', null, Number(vista.ventas.iva_usd), Number(vista.ventas.iva_bs)],
      ['Propinas', null, Number(vista.ventas.propina_usd), Number(vista.ventas.propina_bs)],
      ...(vista.fiscal ? [
        { valores: ['Fiscal', vista.fiscal.tickets, null, null], negrita: true },
        ['Base', null, Number(vista.fiscal.base_usd), Number(vista.fiscal.base_bs)],
        ['IVA declarado', null, Number(vista.fiscal.iva_usd), Number(vista.fiscal.iva_bs)],
        ['Total fiscal', null, Number(vista.fiscal.total_usd), Number(vista.fiscal.total_bs)]
      ] : []),
      ...(vista.paralelo ? [
        { valores: ['Paralelo', vista.paralelo.tickets, null, null], negrita: true },
        ['Venta paralelo', null, Number(vista.paralelo.venta_usd), Number(vista.paralelo.venta_bs)]
      ] : [])
    ] : [];
    return {
      titulo: 'Resumen del día de caja',
      detalle: `${formatFecha(fecha)} · ${nombrePestana}`,
      hojas: [
        { nombre: 'Resumen', columnas: cols, filas: resumen },
        {
          nombre: 'Por forma de pago', columnas: cols,
          filas: (vista?.formas || []).map((f) => [`${f.nombre}${f.fiscal === false ? ' (paralelo)' : ''}`, null, Number(f.total_usd), Number(f.total_bs)])
        },
        {
          nombre: 'Por canal', columnas: cols,
          filas: (vista?.canales || []).map((c) => [CANAL_LABEL[c.canal] || c.canal, c.tickets, Number(c.total_usd), Number(c.total_bs)])
        },
        {
          nombre: 'Tickets',
          columnas: [{ titulo: 'Nº' }, { titulo: 'Hora' }, { titulo: 'Cajero' }, { titulo: 'Canal' }, { titulo: '$', tipo: 'monto' }, { titulo: 'Bs', tipo: 'monto' }, { titulo: 'Estado' }],
          filas: tickets.map((t) => [
            `#${t.numero}`, hora(t.fecha), t.cajero || '', `${CANAL_LABEL[t.canal] || t.canal}${t.canal === 'delivery' ? ` · ${t.plataforma || 'Sin definir'}` : ''}`,
            Number(t.total_usd), Number(t.total_bs), t.estado === 'anulada' ? 'Anulado' : 'Cobrado'
          ])
        }
      ]
    };
  }

  return (
    <div className="space-y-6">
      <EncabezadoImpresion titulo="Resumen del día de caja" detalle={`${formatFecha(fecha)} · ${nombrePestana}`} />
      <div className="flex flex-wrap items-center justify-between gap-3 print:hidden">
        <h1 className="text-2xl font-bold text-slate-800">
          Caja · Resumen del día
          {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
          <BotonAyuda />
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date" value={fecha} max={hoyCaracas()}
            onChange={(e) => e.target.value && setFecha(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          />
          <Exportar excel={contenidoExcel} disabled={!datos || datos.turnos.length === 0} />
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2 text-sm">{error}</div>}

      {datos && (
        <>
          <div className="flex gap-1 border-b print:hidden">
            {pestanas.map((p) => (
              <button
                key={p.key}
                onClick={() => { setPestana(p.key); setAbierto(null); }}
                className={`px-3.5 py-2.5 text-sm ${pestana === p.key ? 'font-semibold text-primary-600 border-b-2 border-primary-600' : 'text-slate-500 hover:text-slate-700'}`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {datos.turnos.length === 0 && (
            <p className="text-sm text-slate-400">No hubo turnos de caja el {formatFecha(datos.fecha)}.</p>
          )}

          {turnoSel && (
            <div className="bg-white rounded-xl shadow-sm p-4 text-sm text-slate-600 flex flex-wrap gap-x-6 gap-y-1">
              <span>Estado: <b className={turnoSel.estado === 'abierto' ? 'text-green-700' : 'text-slate-700'}>{turnoSel.estado === 'abierto' ? 'Abierto' : 'Cerrado'}</b></span>
              <span>Abierto: {hora(turnoSel.abierto_en)}{turnoSel.abierto_por ? ` · ${turnoSel.abierto_por}` : ''}</span>
              {turnoSel.cerrado_en && <span>Cerrado: {hora(turnoSel.cerrado_en)}{turnoSel.cerrado_por ? ` · ${turnoSel.cerrado_por}` : ''}</span>}
              <span>Tasa BCV: {formatMonto(turnoSel.tasa_bcv, 4)}</span>
              <span>Fondo inicial: Bs {formatMonto(turnoSel.efectivo.fondo_inicial_bs)} · $ {formatMonto(turnoSel.efectivo.fondo_inicial_usd)}</span>
              {turnoSel.digital && Number(turnoSel.digital.fondo_bs) > 0 && (
                <span>Saldo digital: Bs {formatMonto(turnoSel.digital.fondo_bs)} · vueltos {formatMonto(turnoSel.digital.vueltos_bs)}</span>
              )}
              {turnoSel.diferencia_bs != null && (
                <span>Arqueo: Bs <b className={Number(turnoSel.diferencia_bs) < 0 ? 'text-red-600' : 'text-slate-700'}>{formatMonto(turnoSel.diferencia_bs)}</b> · $ <b className={Number(turnoSel.diferencia_usd) < 0 ? 'text-red-600' : 'text-slate-700'}>{formatMonto(turnoSel.diferencia_usd)}</b></span>
              )}
              {turnoSel.motivo_cierre_tardio && <span className="w-full text-red-700">Cierre tardío: {turnoSel.motivo_cierre_tardio}</span>}
            </div>
          )}

          {vista && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Tarjeta etiqueta="Tickets cobrados" valor={`${vista.ventas.tickets}${vista.ventas.anuladas ? ` · ${vista.ventas.anuladas} anulados` : ''}`} />
                <Tarjeta etiqueta="Total vendido" usd={vista.ventas.total_usd} bs={vista.ventas.total_bs} />
                <Tarjeta etiqueta="IVA" usd={vista.ventas.iva_usd} bs={vista.ventas.iva_bs} />
                <Tarjeta etiqueta="Propinas" usd={vista.ventas.propina_usd} bs={vista.ventas.propina_bs} />
              </div>

              <DivisionFiscal fiscal={vista.fiscal} paralelo={vista.paralelo} />

              <div className="grid lg:grid-cols-2 gap-4 items-start">
                <TablaMontos
                  titulo="Por forma de pago"
                  vacio="Sin pagos registrados."
                  filas={vista.formas.map((f, i) => ({
                    clave: f.id || `${f.nombre}-${i}`,
                    nombre: (
                      <span>
                        {f.nombre}
                        {f.fiscal === false && <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-800 text-white">Paralelo</span>}
                      </span>
                    ),
                    total_usd: f.total_usd, total_bs: f.total_bs
                  }))}
                />
                <TablaMontos
                  titulo="Por canal"
                  vacio="Sin ventas."
                  filas={(vista.canales || []).map((c) => ({ clave: c.canal, nombre: CANAL_LABEL[c.canal] || c.canal, tickets: c.tickets, total_usd: c.total_usd, total_bs: c.total_bs }))}
                />
              </div>
            </>
          )}

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <h2 className="font-semibold text-slate-700 p-4 border-b">Tickets</h2>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-left">
                <tr>
                  <th className="p-3 w-8" />
                  <th className="p-3">Nº</th>
                  <th className="p-3">Hora</th>
                  <th className="p-3">Cajero</th>
                  <th className="p-3">Canal</th>
                  <th className="p-3 text-right">$</th>
                  <th className="p-3 text-right">Bs</th>
                  <th className="p-3">Estado</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => (
                  <React.Fragment key={t.id}>
                    <tr {...filaClickeable(() => setAbierto(abierto === t.id ? null : t.id))} className={`border-t cursor-pointer hover:bg-slate-50 ${FOCO_FILA}`}>
                      <td className="p-3 text-slate-400">{abierto === t.id ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</td>
                      <td className="p-3 font-medium">#{t.numero}</td>
                      <td className="p-3">{hora(t.fecha)}</td>
                      <td className="p-3">{t.cajero || '—'}</td>
                      <td className="p-3">{CANAL_LABEL[t.canal] || t.canal}{t.canal === 'delivery' ? ` · ${t.plataforma || 'Sin definir'}` : ''}</td>
                      <td className="p-3 text-right tabular-nums">{formatMonto(t.total_usd)}</td>
                      <td className="p-3 text-right tabular-nums">{formatMonto(t.total_bs)}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${t.estado === 'anulada' ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                          {t.estado === 'anulada' ? 'Anulado' : 'Cobrado'}
                        </span>
                      </td>
                    </tr>
                    {abierto === t.id && (
                      <tr className="bg-slate-50">
                        <td colSpan={8} className="p-4"><DetalleTicket id={t.id} /></td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {tickets.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-slate-400">Sin tickets.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
